/**
 * SuccessAlert Component
 *
 * Consistent success message display across admin components.
 * Replaces ~50 lines of duplicate success UI code.
 *
 * @example
 * ```tsx
 * <SuccessAlert message={success} onDismiss={() => setSuccess(null)} />
 * ```
 */

import { CheckCircle, X } from 'lucide-react';

interface SuccessAlertProps {
  /** Success message to display */
  message: string | null;

  /** Optional dismiss handler */
  onDismiss?: () => void;

  /** Optional title (default: "Success") */
  title?: string;

  /** Auto-dismiss after milliseconds (0 = no auto-dismiss) */
  autoDismiss?: number;
}

export function SuccessAlert({
  message,
  onDismiss,
  title = 'Success',
  autoDismiss = 0
}: SuccessAlertProps) {
  // Auto-dismiss effect
  if (autoDismiss > 0 && message && onDismiss) {
    setTimeout(() => {
      onDismiss();
    }, autoDismiss);
  }

  if (!message) return null;

  return (
    <div className="mb-6 border-l-4 border-green-400 bg-green-50 p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-400" />
          <div className="flex-1">
            {title && (
              <h3 className="mb-1 font-semibold text-green-700">
                {title}
              </h3>
            )}
            <p className="text-sm text-green-700">{message}</p>
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="ml-4 text-green-400 hover:opacity-70"
            aria-label="Dismiss success message"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
